import React, { ChangeEvent, useCallback, useEffect, useMemo } from "react";
import { Controller, FormContext, FormContextValues } from "react-hook-form";
import { TextField, MenuItem, Grid, withStyles } from "@material-ui/core";
import { WrapNotesTypes, Subcategory } from "../../../api/WrapNotes";
import { FIELD_REQUIRED_MSG } from "../../../utils/validation";
import { DropdownSelector } from "../../../components/Forms";

export interface WrapNotesFormProps {
  formContext: FormContextValues<WrapNotesFormValues>;
  onPersist: (values: Record<string, any>) => void;
  wrapNotesTypes: WrapNotesTypes;
}

export interface WrapNotesFormValues {
  category?: string;
  subcategory?: string;
  summary?: string;
  query?: string;
  response?: string;
  status: string;
  contactType: string;
}

const BorderlessTextField = withStyles((theme) => ({
  root: {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        border: 0,
      },
    },
    "& .Mui-disabled": {
      color: theme.palette.text.primary,
    },
  },
}))(TextField);

export const WrapNotesForm: React.FC<WrapNotesFormProps> = ({
  formContext,
  onPersist,
  wrapNotesTypes,
}) => {
  const {
    register,
    errors,
    formState,
    control,
    getValues,
    setValue,
    clearError,
    watch,
  } = formContext;

  const handleCategoryChange = useCallback(
    (args) => {
      const event: ChangeEvent<HTMLInputElement> = args[0];

      // Clear the subcategory when category has changed
      clearError("subcategory"); // in case the field was in error
      setValue("subcategory", "");

      return event.target.value;
    },
    [clearError, setValue],
  );

  const handlePersist = useCallback(() => {
    const values = getValues();
    onPersist(values);
  }, [getValues, onPersist]);

  // Ensures a re-render happens when these values change
  watch(["category", "subcategory"]);

  // Current Form Values
  const { dirty } = formState;
  const { category, subcategory } = getValues();

  // Derive the correct list of subcategories based on category selection
  const { categories, contactTypes } = wrapNotesTypes;
  const orderedContactTypes = Object.values(contactTypes).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  const selectedContactType = watch("contactType");
  const summary = watch("summary");
  const query = watch("query");
  const response = watch("response");
  const contactType = contactTypes[selectedContactType] || {
    queryAndResponseFlag: false,
  };

  const subcategories: Subcategory[] = useMemo(() => {
    if (!category) return [];

    return categories[category]
      ? Object.values(categories[category].subcategories).sort((a, b) =>
          a.name.localeCompare(b.name),
        )
      : [];
  }, [categories, category]);

  // Persist values when selects change
  // The text input fields use the onBlur handler directly for performance reasons
  useEffect(() => {
    if (dirty) {
      handlePersist();
    }
  }, [category, subcategory, dirty, handlePersist]);

  useEffect(() => {
    if (contactType?.queryAndResponseFlag) {
      if (summary) {
        setValue("query", summary);
      }
    } else {
      if (query && response) {
        setValue("summary", query + "\n" + response);
      } else if (query && !response) {
        setValue("summary", query);
      } else if (response && !query) {
        setValue("summary", response);
      }
    }
  }, [
    contactType,
    contactType.queryAndResponseFlag,
    query,
    response,
    setValue,
    summary,
  ]);

  return (
    <FormContext {...formContext}>
      <Grid container direction="row" justify="flex-start" spacing={4}>
        <Grid item xs={6}>
          <Controller
            control={control}
            rules={{ required: true }}
            name="category"
            onChange={handleCategoryChange}
            defaultValue=""
            as={
              <TextField
                fullWidth
                size="small"
                label="Category"
                variant="outlined"
                required
                error={!!errors.category}
                helperText={!!errors.category ? FIELD_REQUIRED_MSG : undefined}
                data-testid="WrapNotesForm__Select--category"
                select
              >
                {Object.values(categories)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((c) => (
                    <MenuItem value={c.id} key={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
              </TextField>
            }
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            control={control}
            rules={{ required: true }}
            name="subcategory"
            defaultValue=""
            as={
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Subcategory"
                disabled={!subcategories.length}
                required
                // NOTE: The !!category makes sure we only show errors if a value the field is actually enabled
                error={!!errors.subcategory && !!category}
                helperText={
                  !!errors.subcategory && !!category
                    ? FIELD_REQUIRED_MSG
                    : undefined
                }
                data-testid="WrapNotesForm__Select--subcategory"
                select
              >
                {subcategories.map((s) => (
                  <MenuItem value={s.id} key={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            }
          />
        </Grid>
        <Grid item xs={6}>
          <DropdownSelector
            name="contactType"
            options={orderedContactTypes.map((type) => ({
              name: type.name,
              value: type.id,
            }))}
            controllerProps={{
              defaultValue: orderedContactTypes.find(
                (type) => type.name === "Phone",
              )?.id,
            }}
            fieldProps={{
              label: "Contact type",
              fullWidth: true,
              error: !!errors.contactType,
              helperText: errors?.contactType ? FIELD_REQUIRED_MSG : undefined,
              required: true,
              "data-testid": "WrapNotesForm__Field--contactType",
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <BorderlessTextField
            fullWidth
            disabled
            variant="outlined"
            size="small"
            name="status"
            label="Status"
            value="Closed"
            data-testid="WrapNotesForm__Field--status"
          />
          <input
            name="status"
            defaultValue="false"
            ref={register}
            type="hidden"
          />
        </Grid>

        {!contactType?.queryAndResponseFlag ? (
          <Grid item xs={12}>
            <TextField
              name="summary"
              label="Summary"
              size="small"
              error={!!errors.summary}
              helperText={!!errors.summary ? FIELD_REQUIRED_MSG : undefined}
              inputRef={register({ required: true })}
              inputProps={{
                "data-testid": "WrapNotesForm__Field--summary",
              }}
              required
              multiline
              variant="outlined"
              defaultValue=""
              fullWidth
              onBlur={handlePersist}
            />
          </Grid>
        ) : (
          <>
            <Grid item xs={12}>
              <TextField
                name="query"
                label="Query"
                size="small"
                error={!!errors.query}
                helperText={!!errors.query ? FIELD_REQUIRED_MSG : undefined}
                inputRef={register({ required: true })}
                inputProps={{
                  "data-testid": "WrapNotesForm__Field--query",
                }}
                required
                multiline
                variant="outlined"
                defaultValue=""
                fullWidth
                onBlur={handlePersist}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="response"
                label="Response"
                size="small"
                error={!!errors.response}
                helperText={!!errors.response ? FIELD_REQUIRED_MSG : undefined}
                inputRef={register({ required: true })}
                inputProps={{
                  "data-testid": "WrapNotesForm__Field--response",
                }}
                required
                multiline
                variant="outlined"
                defaultValue=""
                fullWidth
                onBlur={handlePersist}
              />
            </Grid>
          </>
        )}
      </Grid>
    </FormContext>
  );
};
